import clsx from 'clsx';
import BrandLogo from './BrandLogo';

/**
 * Dark side panel shown beside the public sign-in and apply forms. The wordmark
 * artwork is white, so the gradient is what keeps it legible.
 *
 * `sticky` holds the panel in view next to a form column taller than the
 * viewport; short forms leave it off so the panel scrolls with the page.
 * `titleAs` exists because the heading outline belongs to the form column on
 * pages that own an <h1>: this copy is marketing, and the panel is
 * display:none below `lg`, so a heading here would disappear on phones.
 */
export default function BrandHero({
  title,
  subtitle,
  titleAs: Title = 'h2',
  footer = 'Genesis Creations Talent Hub',
  sticky = false,
  contentClassName = 'max-w-sm',
  children,
}) {
  return (
    <div
      className={clsx(
        'relative hidden lg:flex flex-col overflow-hidden p-10 xl:p-12 bg-gradient-to-br from-brand-800 via-brand-700 to-brand-900 text-white',
        sticky && 'lg:sticky lg:top-0 lg:h-screen lg:self-start'
      )}
    >
      <div aria-hidden className="pointer-events-none absolute -left-24 -top-28 h-96 w-96 rounded-full bg-accent-400/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -right-10 h-96 w-96 rounded-full bg-brand-500/20 blur-3xl" />
      {/* Eases the hard edge where this panel meets the light form column. */}
      <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-28 bg-gradient-to-r from-transparent to-white/[0.05]" />

      {/* min-h-0 makes this the region that clips when a short window cannot
          hold the copy, so the footer links below stay reachable. */}
      <div className={clsx('relative min-h-0', contentClassName)}>
        <div className="relative w-[220px] xl:w-[260px]">
          {/* Deepens the panel directly behind the mark: the darker end of the
              blue arc is close to the panel gradient and needs the contrast to
              keep its edge. */}
          <div aria-hidden className="pointer-events-none absolute -inset-12 rounded-full bg-brand-900/60 blur-2xl motion-safe:animate-brand-glow" />
          <BrandLogo
            variant="darkBg"
            size={220}
            className="relative w-full motion-safe:animate-brand-float"
          />
        </div>
        <Title className="text-2xl xl:text-3xl font-semibold mt-8 xl:mt-10 leading-tight">{title}</Title>
        {subtitle && <p className="text-brand-100 mt-3 text-sm leading-relaxed xl:text-base">{subtitle}</p>}
        {children}
      </div>
      {/* mt-auto rather than justify-between on the panel: when flex content
          outgrows its container, justify-between degrades to flex-start and the
          footer — which holds the only links out of this page — is the first
          thing pushed out of sight. */}
      {footer && <p className="mt-auto pt-8 text-xs text-brand-200">{footer}</p>}
    </div>
  );
}
